import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

import { ensureFontsLoaded, getFontList } from 'js/src/data/fonts';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { ReadonlyFontAsset } from 'js/src/data/fonts/fonts-types';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { addTemplateFontsOperation } from 'js/src/luminary/template-font-operations';

import { getGlobalFontManager } from '@lumen5/luminary';

const MAX_ADDED_FONTS = 2;

export const addFontsSchema = z.object({
    fonts: z
        .array(z.string())
        .max(MAX_ADDED_FONTS)
        .optional()
        .describe(
            'Font library names, exactly as listed. Omit to list the library and the fonts the video already has.',
        ),
});
export type AddFontsArgs = z.infer<typeof addFontsSchema>;

const libraryName = (font: ReadonlyFontAsset) => font.name.trim();

export class AddFontsTool extends Tool<typeof addFontsSchema> {
    protected override readonly schema = addFontsSchema;
    protected override readonly toolName = TOOL_NAMES.addFonts;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        "Add up to two fonts from the font library after the video's template fonts, which stay " +
        'where they are. Returns which var(--l5-font-N) carries each font.';

    public getToolDisplayName(): string {
        return 'Choosing fonts';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(
        args: AddFontsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        await this.dispatch(ensureFontsLoaded());
        const allFonts = new PlainJsStoreORM(getFontList(this.getState() as RootState));
        const videoFonts = this.luminary.template.fonts;
        const library = allFonts.filter((font) => !videoFonts.includes(font.url));

        if (!args.fonts?.length) {
            return {
                result: [
                    "The video's fonts:",
                    ...this.describeVideoFonts(allFonts),
                    'Font library:',
                    library.map(libraryName).join(', '),
                ].join('\n'),
                isSuccess: true,
            };
        }

        const matches = args.fonts.map((name) => ({
            name,
            font: library.find(
                (font) => libraryName(font).toLowerCase() === name.trim().toLowerCase(),
            ),
        }));
        const unknown = matches.filter(({ font }) => !font).map(({ name }) => name);
        if (unknown.length > 0) {
            return {
                result: `Not in the font library: ${unknown.join(', ')}. Call ${TOOL_NAMES.addFonts} without fonts to list it.`,
                isSuccess: false,
            };
        }

        const urls = [...new Set(matches.flatMap(({ font }) => (font ? [font.url] : [])))];
        const { addTemplateFonts } = makeOperationsCallable(
            { addTemplateFontsOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        addTemplateFonts(urls);

        return {
            result: ["The video's fonts:", ...this.describeVideoFonts(allFonts)].join('\n'),
            isSuccess: true,
        };
    }

    private describeVideoFonts(allFonts: PlainJsStoreORM<ReadonlyFontAsset, number>): string[] {
        const fontManager = getGlobalFontManager();
        return this.luminary.template.fonts.map((url, index) => {
            const inLibrary = allFonts.find((font) => font.url === url);
            const name = inLibrary
                ? libraryName(inLibrary)
                : (fontManager.getFontBySource(url) ?? fontManager.addFont(url))?.familyName;
            return `- var(--l5-font-${index}): ${name ?? url}`;
        });
    }
}
